/**
 * Stores: the warehouse, any shops, and every van.
 *
 * A van is a store with a driver. Giving it a salesman is what tells the
 * route-sales phone (5.5) which stock is his to sell; one salesman drives
 * one van at a time, and the server enforces it.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Skeleton, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import WarehouseIcon from '@mui/icons-material/WarehouseOutlined'
import VanIcon from '@mui/icons-material/LocalShippingOutlined'
import { useTranslation } from 'react-i18next'

import { fetchSalesmen, fetchStores, saveStore } from '../api/endpoints'
import LedgerPicker from '../vouchers/LedgerPicker'
import { errorMessage } from '../api/client'
import { P, type InvStore, type StoreKind } from '../api/types'
import { useAuth } from '../store/AuthContext'
import ScreenTitle from '../components/ScreenTitle'
import { c, fmtMoney } from '../theme'

export default function StoresPage() {
  const { t, i18n } = useTranslation()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const mayEdit = can(P.inventoryMasters)
  const [editing, setEditing] = useState<InvStore | 'new' | null>(null)
  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const salesmen = useQuery({ queryKey: ['inv-salesmen'], queryFn: fetchSalesmen,
                              enabled: mayEdit })
  const who = (id?: number | null) => {
    const u = salesmen.data?.find((x) => x.id === id)
    return u ? (u.full_name || u.username) : null
  }

  return (
    <Stack spacing={2}>
      <ScreenTitle titleKey="nav.stores" inset={false} sub={t('inv.storesSub')}
        right={mayEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('inv.newStore')}</Button>
        )} />
      {stores.isError && <Alert severity="warning">{errorMessage(stores.error)}</Alert>}
      {stores.isLoading ? <Skeleton variant="rectangular" height={120} /> : (
        <Box sx={{ display: 'grid', gap: 2,
                   gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {(stores.data ?? []).map((s) => {
            const Icon = s.kind === 'VAN' ? VanIcon : WarehouseIcon
            return (
              <Paper key={s.id} variant="outlined" component={mayEdit ? 'button' : 'div'}
                onClick={mayEdit ? () => setEditing(s) : undefined}
                sx={{ p: 2.25, textAlign: 'start', font: 'inherit', color: 'inherit',
                      cursor: mayEdit ? 'pointer' : 'default', opacity: s.is_active ? 1 : 0.55,
                      display: 'flex', flexDirection: 'column', gap: 1,
                      '&:hover': mayEdit ? { borderColor: c.borderStrong } : {},
                      '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid',
                    placeItems: 'center', bgcolor: s.kind === 'VAN' ? c.warningSoft : c.primarySoft,
                    color: s.kind === 'VAN' ? c.warningInk : c.primaryInk }}>
                    <Icon fontSize="small" /></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap dir="auto" sx={{ fontWeight: 600, fontSize: 15 }}>
                      {(ar && s.name_ar) || s.name_en}</Typography>
                    <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                      <span className="doc-no">{s.code}</span> · {t(`inv.kind${s.kind}`)}
                      {!s.is_active && <> · {t('inv.closed')}</>}
                    </Typography>
                  </Box>
                </Stack>
                {s.kind === 'VAN' && (
                  <Typography sx={{ fontSize: 13, color: s.salesman_user_id ? c.inkSecondary : c.warningInk }}>
                    {s.salesman_user_id ? t('inv.drivenBy', { name: who(s.salesman_user_id) ?? '…' })
                                        : t('inv.noSalesman')}
                    <span style={{ display: 'block', fontSize: 12, color: s.cash_ledger_code
                      ? 'var(--ink-muted)' : 'var(--warning-ink)' }}>
                      <span className="doc-no">{s.series_prefix}-INV</span> · {s.cash_ledger_name
                        ?? t('van.noVanCash')}</span>
                  </Typography>
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ borderTop: `1px solid ${c.divider}`,
                  pt: 1, fontSize: 13, color: c.inkSecondary }}>
                  <span>{t('inv.itemsHeld', { count: s.item_count ?? 0 })}</span>
                  {s.stock_value !== undefined && (
                    <span className="num" style={{ fontWeight: 600, color: 'var(--ink)' }}>
                      SAR {fmtMoney(s.stock_value)}</span>
                  )}
                </Stack>
              </Paper>
            )
          })}
        </Box>
      )}
      {editing && (
        <StoreDialog store={editing === 'new' ? null : editing} salesmen={salesmen.data ?? []}
          onClose={() => setEditing(null)} />
      )}
    </Stack>
  )
}

function StoreDialog({ store, salesmen, onClose }: {
  store: InvStore | null
  salesmen: { id: number; username: string; full_name?: string | null }[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [form, setForm] = useState({
    code: store?.code ?? '', name_en: store?.name_en ?? '', name_ar: store?.name_ar ?? '',
    kind: (store?.kind ?? 'WAREHOUSE') as StoreKind,
    salesman_user_id: store?.salesman_user_id ?? null as number | null,
    doc_prefix: store?.doc_prefix ?? '',
    cash_ledger_code: store?.cash_ledger_code ?? '',
    is_active: store?.is_active ?? true,
  })
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => saveStore({ ...form, code: form.code.trim().toUpperCase(),
      name_ar: form.name_ar.trim() || null,
      salesman_user_id: form.kind === 'VAN' ? form.salesman_user_id : null,
      doc_prefix: form.kind === 'VAN' ? (form.doc_prefix.trim().toUpperCase() || null) : null,
      cash_ledger_code: form.kind === 'VAN' ? (form.cash_ledger_code || null) : null }, store?.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inv-stores'] }); onClose() },
    onError: (e) => setError(errorMessage(e)),
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{store ? store.name_en : t('inv.newStore')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <ToggleButtonGroup exclusive fullWidth size="small" value={form.kind}
            onChange={(_, v) => v && setForm({ ...form, kind: v })}>
            <ToggleButton value="WAREHOUSE"><WarehouseIcon fontSize="small" sx={{ mr: 1 }} />
              {t('inv.kindWAREHOUSE')}</ToggleButton>
            <ToggleButton value="VAN"><VanIcon fontSize="small" sx={{ mr: 1 }} />
              {t('inv.kindVAN')}</ToggleButton>
          </ToggleButtonGroup>
          <TextField label={t('inv.code')} value={form.code} required autoFocus
            inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
            helperText={form.kind === 'VAN' ? t('inv.vanCodeHint') : undefined}
            onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <TextField label={t('inv.nameEn')} value={form.name_en} required
            onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
          <TextField label={t('inv.nameAr')} value={form.name_ar} inputProps={{ dir: 'rtl' }}
            onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
          {form.kind === 'VAN' && (
            <TextField select label={t('inv.salesman')} value={form.salesman_user_id ?? ''}
              helperText={t('inv.salesmanHint')}
              onChange={(e) => setForm({ ...form, salesman_user_id:
                e.target.value === '' ? null : Number(e.target.value) })}>
              <MenuItem value="">{t('inv.noneYet')}</MenuItem>
              {salesmen.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.full_name || u.username}</MenuItem>
              ))}
            </TextField>
          )}
          {form.kind === 'VAN' && (
            <>
              <TextField label={t('van.seriesPrefix')} value={form.doc_prefix}
                placeholder={form.code.trim().toUpperCase() || 'V01'}
                helperText={t('van.seriesHint', { prefix: (form.doc_prefix.trim() || form.code.trim()
                  || 'V01').toUpperCase(), year: new Date().getFullYear() })}
                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                onChange={(e) => setForm({ ...form, doc_prefix: e.target.value.replace(/[^A-Za-z0-9]/g, '') })} />
              <Box>
                <Typography sx={{ fontSize: 12, color: c.inkMuted, mb: 0.5 }}>{t('van.vanCash')}</Typography>
                <LedgerPicker value={form.cash_ledger_code} groups={['CASH']}
                  placeholder={t('van.vanCashPlaceholder')}
                  onChange={(code) => setForm({ ...form, cash_ledger_code: code })} />
                <Typography sx={{ fontSize: 12, color: c.inkMuted, mt: 0.5 }}>{t('van.vanCashHint')}</Typography>
              </Box>
            </>
          )}
          {store && (
            <FormControlLabel control={<Switch checked={form.is_active}
              onChange={(_, v) => setForm({ ...form, is_active: v })} />}
              label={form.is_active ? t('inv.storeOpen') : t('inv.storeClosed')} />
          )}
          {store && !form.is_active && store.is_active && (
            <Chip size="small" color="warning" variant="outlined" label={t('inv.closeHint')} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => save.mutate()}
          disabled={save.isPending || !form.code.trim() || !form.name_en.trim()}>
          {t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  )
}
